/**
 * High-precision SVG Path with progressive handwriting/drawing and morphing.
 */
import { SVGObject, SVGObjectConfig } from './SVGObject';
import { Renderer } from '../core/Renderer';
import { Interpolator } from '../core/Interpolator';

export interface PathProps {
  d: string;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  strokeLinecap?: 'butt' | 'round' | 'square';
  strokeLinejoin?: 'miter' | 'round' | 'bevel';
  drawProgress?: number; // 0 to 1
  morphTarget?: string;
  morphProgress?: number; // 0 to 1
}

export class PathObject extends SVGObject {
  public props: PathProps;
  private pathEl: SVGPathElement | null = null;
  private totalLength: number = 0;

  constructor(config: SVGObjectConfig & { props?: PathProps }) {
    super({ ...config, type: 'path' });
    this.props = {
      d: 'M 0 0 L 100 100',
      fill: 'none',
      stroke: '#38bdf8',
      strokeWidth: 3,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      drawProgress: 1,
      ...config.props
    };
  }

  public render(): void {
    this.container.innerHTML = '';
    const path = Renderer.createElement('path');

    let currentD = this.props.d;
    if (this.props.morphTarget && this.props.morphProgress !== undefined && this.props.morphProgress > 0) {
      currentD = Interpolator.lerpPath(this.props.d, this.props.morphTarget, this.props.morphProgress);
    }

    Renderer.setAttributes(path, {
      d: currentD,
      fill: this.props.fill || 'none',
      stroke: this.props.stroke || '#38bdf8',
      'stroke-width': this.props.strokeWidth || 3,
      'stroke-linecap': this.props.strokeLinecap || 'round',
      'stroke-linejoin': this.props.strokeLinejoin || 'round'
    });

    this.pathEl = path;
    this.container.appendChild(path);
    this.measureAndApplyProgress();
  }

  public setD(d: string): void {
    this.props.d = d;
    this.render();
  }

  public setDrawProgress(progress: number): void {
    this.props.drawProgress = Math.max(0, Math.min(1, progress));
    this.measureAndApplyProgress();
  }

  public setMorphProgress(progress: number): void {
    this.props.morphProgress = Math.max(0, Math.min(1, progress));
    this.render();
  }

  private measureAndApplyProgress(): void {
    if (!this.pathEl) return;
    try {
      if (this.totalLength === 0 && this.pathEl.getTotalLength) {
        this.totalLength = this.pathEl.getTotalLength();
      }
      if (this.totalLength > 0) {
        const offset = this.totalLength * (1 - (this.props.drawProgress ?? 1));
        this.pathEl.style.strokeDasharray = `${this.totalLength}`;
        this.pathEl.style.strokeDashoffset = `${offset}`;
      }
    } catch {
      // SVG not in DOM tree yet
    }
  }

  public getTotalLength(): number {
    return this.totalLength;
  }
}
